import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChangeValue, ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberSupportNegativeFilter, percentageSignFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import { CHART_HEIGHT } from "app/pages/industry-analysis/segmentation/size-and-growth-overview/common/const";
import { tooltipPositioner } from "app/pages/website-analysis/traffic-sources/mmx/components/popCompareTooltipPositioner";
import {
    columnChartConfig,
    EChartDisplayType,
} from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";

import type { IDomainsChangeValues, ISeries } from "./types";
import { getCategoriesItems } from "./utils";

const TooltipWrapper = styled.div`
    padding: 16px;
    box-sizing: border-box;
`;

const ChangeCellWrapper = styled.div`
    ${setFontVariant("body2", colorsPalettes.carbon[500])};
    display: flex;
    justify-content: right;
`;

const ChangeCell = styled.div`
    align-self: center;
`;

const getRows = (rowData: ISeries[], index: number) => {
    return rowData.map((singleRowData) => ({
        domain: {
            label: singleRowData.name,
            backgroundColor: singleRowData.color,
        },
        currentPeriod: {
            value: singleRowData.data?.[index]?.values[0]?.value,
        },
        comparedPeriod: {
            value: singleRowData.data?.[index]?.values[1]?.value,
        },
        change: {
            value: singleRowData.data?.[index]?.y,
        },
    }));
};

interface TooltipRow {
    domain: { label: string; background?: string; backgroundColor?: string };
    currentPeriod: { value: string | number };
    comparedPeriod: { value: string | number };
    change: { value: string | number };
}

const getColumnsDefinition = (isPercentage: boolean, durations: Array<string>): Array<ColumnDefinition<TooltipRow>> => {
    return [
        {
            key: "1",
            title: translate("mmx.channelsoverview.channels.chart.tooltip.domain"),
            selector: "domain" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ domain }) => (
                <ChartTooltip.LabelCell
                    label={domain.label}
                    backgroundColor={domain.backgroundColor}
                />
            ),
        },
        {
            key: "2",
            title: translate(durations[0]),
            selector: "currentPeriod" as const,
            align: "right",
            borderRight: true,
            size: ChartTooltip.ValueAndChangeCellDefaultWidth,
            render: ({ currentPeriod }) => (
                <ChartTooltip.ValueCell
                    // @ts-expect-error FIXME: Argument of type 'string | number' is not assignable to parameter of type 'number'
                    value={abbrNumberSupportNegativeFilter()(currentPeriod.value, 0)}
                />
            ),
        },
        {
            key: "3",
            title: translate(durations[1]),
            selector: "comparedPeriod" as const,
            align: "right",
            borderRight: true,
            size: ChartTooltip.ValueAndChangeCellDefaultWidth,
            render: ({ comparedPeriod }) => (
                <ChartTooltip.ValueCell
                    // @ts-expect-error FIXME: Argument of type 'string | number' is not assignable to parameter of type 'number'
                    value={abbrNumberSupportNegativeFilter()(comparedPeriod.value, 0)}
                />
            ),
        },
        {
            key: "4",
            title: translate(
                isPercentage
                    ? "mmx.channelsoverview.channels.chart.tooltip.change"
                    : "website_analysis.marketing_channels.channels.overview.tooltip.abs.change.title",
            ),
            selector: "change" as const,
            align: "right",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({ change }) => {
                const value = isPercentage
                    ? percentageSignFilter()(change.value, 2)
                    : // @ts-expect-error FIXME: Argument of type 'string | number' is not assignable to parameter of type 'number'
                      abbrNumberSupportNegativeFilter()(change.value, 0);
                const isDecrease = Number(change.value) < 0;

                return (
                    <ChangeCellWrapper>
                        <ChangeCell>
                            <ChangeValue
                                value={value}
                                isDecrease={isDecrease}
                            />
                        </ChangeCell>
                    </ChangeCellWrapper>
                );
            },
        },
    ];
};

export const getChartConfig = (
    chartData: ISeries[],
    isPercentage: boolean,
    durations: Array<string>,
    domainsChangeValues: IDomainsChangeValues,
) => {
    const tooltipFormatter = (highChartRef) => {
        const { point } = highChartRef;
        const { index } = point;
        return (
            <TooltipWrapper>
                <ChartTooltip<TooltipRow>
                    data={getRows(chartData, index)}
                    columnDefinition={getColumnsDefinition(isPercentage, durations)}
                    width={600}
                    title={point?.options?.key}
                />
            </TooltipWrapper>
        );
    };

    const mainSeries = chartData[0];
    if (!mainSeries) {
        return {};
    }
    const { data: mainSeriesData = [] } = mainSeries;
    const columnChartConfigArgs = {
        maxPointWidth: 40,
        height: CHART_HEIGHT,
        displayDataLabelsOnColumns: true,
        tooltipFormatter: tooltipFormatter,
        chartDisplayType: isPercentage ? EChartDisplayType.PERCENTAGE : EChartDisplayType.ABSOLUTE,
        renderCustomCategories: (index) => getCategoriesItems(mainSeriesData, domainsChangeValues, isPercentage)[index],
        yAxisFormatter: ({ value }) =>
            isPercentage ? yAxisChangeFilter()(value, 2) : abbrNumberSupportNegativeFilter()(value),
        tooltipConfig: {
            outside: true,
            positioner: tooltipPositioner,
        },
    };
    return columnChartConfig(columnChartConfigArgs);
};
